import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import {
  browserInterventionsChannel,
  GetHumanTasksResponse,
  AcceptHumanTaskResponse,
  PickNextTaskResponse,
  HumanTask,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import { useCompany } from '../../contexts/CompanyContext';
import { PUSHER_ENV } from '../../lib/pusherClient';
import OperatorLanguages from './OperatorLanguages';
import { formatLanguageTag } from './humanTaskLanguage';
import {
  interventionOperatorHref,
  usesOperatorPage,
} from './interventionOperatorHref';
import {
  RequesterBadge,
  RequesterTask,
  compareTasksForDisplay,
  requesterTileClass,
} from './humanTaskRequester';
import BrowserInterventionsSection, {
  Intervention,
  activeInterventions,
  myInterventions,
  pendingInterventions as sortPendingInterventions,
} from './BrowserInterventions';
import {
  HumanTaskIdleResponseRequest,
  HumanTaskIdleResponseResponse,
  IdleActivityPill,
  IdleTrackedTask,
  NeedsAttentionStrip,
  RetryBadge,
  idleTileClass,
} from './humanTaskIdle';
import {
  credentialFieldText,
  maskCredentialValue,
} from '../../utils/credentialFieldValue';
import './human-tasks.scss';

interface RevealedCredential {
  credentialId: string;
  label: string;
  site: string | null;
  credentialType: string;
  fields: Record<string, unknown> | null;
  error?: string;
}

// Issue reports (help_request) are internal escalations raised by assistants,
// not delegation work. They are hidden from the tasks screen for everyone
// except the people responsible for looking into and reporting back on them.
const ISSUE_REPORT_HANDLER_USER_IDS = new Set<string>([
  'auth0|66a8f7f6d8685a3fff60d84b',
]);

const TASK_REFRESH_INTERVAL_MS = 60_000;

const LANGUAGE_FIT_TITLE: Record<string, string> = {
  exact: 'You speak this language',
  language: 'You speak this language (different region)',
  none: 'Not one of your languages',
};

// Phone-call tasks only: the language the worker must speak. NULL means the
// default (English). For pending tasks the backend also says how the task's
// language fits the signed-in operator's languages.
const TaskLanguage = ({ task }: { task: HumanTask }) => {
  if (task.taskType !== 'phone_call') return null;
  const name = task.language ? formatLanguageTag(task.language) : 'English';
  const fit = task.languageFit;
  return (
    <span
      className={`task-language ${fit ? `task-language--fit-${fit}` : ''}`}
      title={fit ? LANGUAGE_FIT_TITLE[fit] : 'Language the worker must speak'}
    >
      {name}
      {task.languageRequirement && (
        <span className='task-language-requirement'>
          {task.languageRequirement}
        </span>
      )}
    </span>
  );
};

// Simple inline SVG icons
const PhoneIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <line x1='2' y1='12' x2='22' y2='12' />
    <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
  </svg>
);

const MapPinIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
    <circle cx='12' cy='10' r='3' />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='9 18 15 12 9 6' />
  </svg>
);

const LifeBuoyIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <circle cx='12' cy='12' r='4' />
    <line x1='4.93' y1='4.93' x2='9.17' y2='9.17' />
    <line x1='14.83' y1='14.83' x2='19.07' y2='19.07' />
    <line x1='14.83' y1='9.17' x2='19.07' y2='4.93' />
    <line x1='14.83' y1='9.17' x2='18.36' y2='5.64' />
    <line x1='4.93' y1='19.07' x2='9.17' y2='14.83' />
  </svg>
);

const CreditCardIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='5' width='20' height='14' rx='2' />
    <line x1='2' y1='10' x2='22' y2='10' />
  </svg>
);

const HumanTasks: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const currentUserId = user?.sub;
  const navigate = useNavigate();
  const { opsRole } = useCompany();
  const isAdmin = opsRole === 'ops_admin';
  const canSeeIssueReports =
    !!currentUserId && ISSUE_REPORT_HANDLER_USER_IDS.has(currentUserId);
  const [activeTasks, setActiveTasks] = useState<RequesterTask[]>([]);
  const [availableTasks, setAvailableTasks] = useState<RequesterTask[]>([]);
  const [issueTasks, setIssueTasks] = useState<RequesterTask[]>([]);
  const [suggestedNextTaskId, setSuggestedNextTaskId] = useState<string | null>(
    null,
  );
  const [completedTasks, setCompletedTasks] = useState<HumanTask[]>([]);
  const [showCompleted, setShowCompleted] = useState<boolean>(false);
  const [completedLoading, setCompletedLoading] = useState<boolean>(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acceptingTaskId, setAcceptingTaskId] = useState<string | null>(null);
  const [releasingTaskId, setReleasingTaskId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [pickingNext, setPickingNext] = useState<boolean>(false);
  const [revealedCreds, setRevealedCreds] = useState<
    Record<string, RevealedCredential[]>
  >({});
  const [credLoading, setCredLoading] = useState<string | null>(null);
  const [credError, setCredError] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  const pusherContext = usePusher(true) as any;

  useEffect(() => {
    loadTasks();
    loadInterventions();
  }, []);

  // Idle pills, the Needs-attention strip and live-call state are server
  // snapshots; refresh them in the background at the idle sweep's cadence.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadTasks({ silent: true });
      }
    }, TASK_REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // Pusher real-time updates for interventions
  useEffect(() => {
    const pusherInstance = pusherContext?.pusherInstance;
    if (!pusherInstance) return;

    const channelName = browserInterventionsChannel(PUSHER_ENV);
    const channel = pusherInstance.subscribe(channelName);
    channel.bind('new-intervention', () => loadInterventions());
    channel.bind('intervention-claimed', () => loadInterventions());
    channel.bind('intervention-resolved', () => loadInterventions());

    return () => {
      channel.unbind_all();
      pusherInstance.unsubscribe(channelName);
    };
  }, [pusherContext?.pusherInstance]);

  const loadInterventions = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/browser-agent/interventions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInterventions(response.data.interventions || []);
    } catch (err) {
      // Silently fail — interventions are supplementary
      console.error('Error loading interventions:', err);
    }
  };

  const handleRevealCredentials = useCallback(
    async (commandId: string) => {
      try {
        setCredLoading(commandId);
        setCredError((prev) => {
          const next = { ...prev };
          delete next[commandId];
          return next;
        });
        const token = await getAccessTokenSilently();
        const response = await api.get(
          `/browser-agent/interventions/${commandId}/credentials/reveal`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.success) {
          setRevealedCreds((prev) => ({
            ...prev,
            [commandId]: response.data.credentials ?? [],
          }));
        } else {
          setCredError((prev) => ({
            ...prev,
            [commandId]: response.data.error || 'Failed to reveal credentials',
          }));
        }
      } catch (err: any) {
        const msg = err.response?.data?.error || 'Failed to reveal credentials';
        setCredError((prev) => ({ ...prev, [commandId]: msg }));
      } finally {
        setCredLoading(null);
      }
    },
    [getAccessTokenSilently],
  );

  const toggleFieldVisibility = useCallback((fieldKey: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  }, []);

  const maskValue = maskCredentialValue;

  // Connect = pick this intervention up: mark it in progress for everyone
  // else's panel. The link itself still opens (anchor default), so a failed
  // claim never blocks the operator from reaching the browser.
  const handleConnectIntervention = (commandId: string) => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          `/browser-agent/interventions/${commandId}/claim`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error claiming intervention:', err);
      } finally {
        loadInterventions();
      }
    })();
  };

  const handleResolveIntervention = async (commandId: string) => {
    try {
      setResolvingId(commandId);
      const token = await getAccessTokenSilently();
      await api.post(
        `/browser-agent/interventions/${commandId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      loadInterventions();
    } catch (err) {
      console.error('Error resolving intervention:', err);
    } finally {
      setResolvingId(null);
    }
  };

  const loadTasks = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const token = await getAccessTokenSilently();

      // Fetch all tasks
      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.tasks) {
        const tasks: RequesterTask[] = response.data.tasks;
        // Separate into active (accepted by current user) and available (pending).
        // Issue reports (help_request) go into their own bucket, shown only to
        // ISSUE_REPORT_HANDLER_USER_IDS in a dedicated section.
        const active = tasks.filter(
          (task) =>
            (task.status === 'accepted' || task.status === 'in_progress') &&
            task.taskType !== 'help_request',
        );
        const available = tasks.filter(
          (task) =>
            task.status === 'pending' && task.taskType !== 'help_request',
        );
        const issues = tasks.filter(
          (task) =>
            task.taskType === 'help_request' &&
            (task.status === 'pending' ||
              task.status === 'accepted' ||
              task.status === 'in_progress'),
        );

        // Priority users first, then soonest (new-user-boosted) deadline,
        // tasks without deadline last — same order pick-next uses.
        setActiveTasks(active.sort(compareTasksForDisplay));
        setAvailableTasks(available.sort(compareTasksForDisplay));
        setIssueTasks(issues.sort(compareTasksForDisplay));
        setSuggestedNextTaskId(response.data.suggestedNextTaskId ?? null);
      } else if (!silent) {
        setError('Failed to load tasks');
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      // Check if it's a 403 forbidden error (no access)
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else if (!silent) {
        setError(err.response?.data?.error || 'Failed to load tasks');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAcceptTask = async (taskId: string) => {
    try {
      setAcceptingTaskId(taskId);
      const token = await getAccessTokenSilently();

      const response = await api.post<AcceptHumanTaskResponse>(
        `/human-tasks/${taskId}/accept`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (
        response.data.success &&
        response.data.threadId &&
        response.data.task
      ) {
        // Navigate to the thread - use 'other' as agent_id since ops users
        // don't have the task creator's agent in their agent list
        navigate(
          `/conversations?agent_id=other&thread_id=${response.data.threadId}&from=tasks`,
        );
      } else {
        setActionError('Failed to accept task');
      }
    } catch (err: any) {
      console.error('Error accepting task:', err);
      setActionError(err.response?.data?.error || 'Failed to accept task');
    } finally {
      setAcceptingTaskId(null);
    }
  };

  const handleReleaseTask = async (task: IdleTrackedTask) => {
    if (releasingTaskId) return;
    const note = window.prompt(
      'Hand this task back to the queue. Optional note for whoever picks it up next:',
      '',
    );
    if (note === null) return;
    try {
      setReleasingTaskId(task.id);
      setActionError(null);
      const token = await getAccessTokenSilently();
      const body: HumanTaskIdleResponseRequest = {
        action: 'release',
        note: note.trim() || undefined,
      };
      const response = await api.post<HumanTaskIdleResponseResponse>(
        `/human-tasks/${task.id}/idle-response`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data.success) {
        setActionError(response.data.error || 'Failed to release task');
      }
    } catch (err: any) {
      console.error('Error releasing task:', err);
      setActionError(err.response?.data?.error || 'Failed to release task');
    } finally {
      setReleasingTaskId(null);
    }
    await loadTasks({ silent: true });
  };

  const handleOpenThread = (threadId: string, readOnly?: boolean) => {
    // Use 'other' as agent_id since ops users access agents from other companies
    const params = `agent_id=other&thread_id=${threadId}`;
    navigate(
      `/conversations?${params}&from=tasks${readOnly ? '&read_only=true' : ''}`,
    );
  };

  // Pending interventions sorted FIFO (oldest first)
  const pendingInterventions = sortPendingInterventions(interventions);
  const inProgressInterventions = activeInterventions(interventions);
  const myActiveInterventions = myInterventions(interventions, currentUserId);

  const hasNextTask =
    pendingInterventions.length > 0 || availableTasks.length > 0;

  const handlePickNext = async () => {
    if (pickingNext) return;
    setPickingNext(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<PickNextTaskResponse>(
        '/human-tasks/pick-next',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;
      if (!data.success) {
        // No tasks available — just refresh the list
        await loadTasks();
        return;
      }

      if (data.type === 'intervention' && data.intervention) {
        const href = interventionOperatorHref(data.intervention);
        if (href) window.open(href, '_blank');
      } else if (data.type === 'task' && data.threadId) {
        navigate(
          `/conversations?agent_id=other&thread_id=${data.threadId}&from=tasks`,
        );
      }

      // Refresh lists after picking
      await Promise.all([loadTasks(), loadInterventions()]);
    } catch (err: any) {
      console.error('Error picking next task:', err);
      if (err.response?.status === 409) {
        // Race condition — another user grabbed it, refresh and retry
        await loadTasks();
      } else {
        setActionError(err.response?.data?.error || 'Failed to pick next task');
      }
    } finally {
      setPickingNext(false);
    }
  };

  const loadCompletedTasks = async () => {
    try {
      setCompletedLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        headers: { Authorization: `Bearer ${token}` },
        params: { includeCompleted: 'true' },
      });

      if (response.data.success && response.data.tasks) {
        const terminal = response.data.tasks.filter(
          (task) =>
            task.status === 'completed' ||
            task.status === 'cancelled' ||
            task.status === 'failed' ||
            task.status === 'handed_off' ||
            task.status === 'closed',
        );
        // Sort by most recently updated
        terminal.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime(),
        );
        setCompletedTasks(terminal);
      }
    } catch (err) {
      console.error('Error loading completed tasks:', err);
    } finally {
      setCompletedLoading(false);
    }
  };

  useEffect(() => {
    if (showCompleted) {
      loadCompletedTasks();
    } else {
      setCompletedTasks([]);
    }
  }, [showCompleted]);

  const formatTaskType = (taskType: string) => {
    if (taskType === 'phone_call') return 'Phone Call';
    if (taskType === 'web_action') return 'Web Action';
    if (taskType === 'in_person') return 'In-Person';
    if (taskType === 'help_request') return 'Issue Report';
    if (taskType === 'hotel_payment') return 'Hotel Payment';
    if (taskType === 'flight_payment') return 'Flight Payment';
    return taskType;
  };

  const formatDeadline = (deadline: string | undefined) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    // Format based on urgency
    if (diffHours < 0) {
      return { text: 'Overdue', className: 'deadline-overdue' };
    } else if (diffHours < 2) {
      return {
        text: `Due in ${Math.ceil(diffHours * 60)} min`,
        className: 'deadline-urgent',
      };
    } else if (diffHours < 24) {
      return {
        text: `Due in ${Math.ceil(diffHours)}h`,
        className: 'deadline-soon',
      };
    } else {
      return {
        text: date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        }),
        className: 'deadline-normal',
      };
    }
  };

  if (loading) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='loading-state'>Loading tasks...</div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='access-denied-state'>
            <h3>Access Denied</h3>
            <p>This page is only accessible to internal ops and super users.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='error-state'>{error}</div>
        </div>
      </div>
    );
  }

  const TaskTypeIcon = ({ taskType }: { taskType: string }) => {
    if (taskType === 'phone_call') return <PhoneIcon />;
    if (taskType === 'web_action') return <GlobeIcon />;
    if (taskType === 'in_person') return <MapPinIcon />;
    if (taskType === 'help_request') return <LifeBuoyIcon />;
    if (taskType === 'hotel_payment') return <CreditCardIcon />;
    if (taskType === 'flight_payment') return <CreditCardIcon />;
    return null;
  };

  // High-impact priority badge — flagged by humanTaskEstimateSpecialist
  // (large purchases, flight bookings, flight check-ins). Backend filters
  // priority=1 tasks out of the ops_worker view, so this badge only ever
  // renders for ops admins.
  const PriorityBadge = ({ priority }: { priority?: number }) =>
    priority === 1 ? (
      <span
        className='task-priority-badge task-priority-p1'
        title='High impact — admin only'
      >
        P1
      </span>
    ) : null;

  // Build the pick-next button label
  const nextIsIntervention = pendingInterventions.length > 0;
  const pendingCount = nextIsIntervention
    ? pendingInterventions.length
    : availableTasks.length;
  const suggestedNextTask = nextIsIntervention
    ? undefined
    : availableTasks.find((t) => t.id === suggestedNextTaskId);
  const pickNextLabel = pickingNext
    ? 'Picking...'
    : !hasNextTask
      ? 'No tasks available'
      : nextIsIntervention
        ? `Browser Intervention (${pendingCount} pending)`
        : `Pick Next Task (${pendingCount} pending)`;

  // Shared pick-next button, with a preview of what it would hand this
  // operator (same language-aware ranking the backend uses to claim).
  const PickNextButton = () => (
    <div className='pick-next'>
      <button
        className={`pick-next-btn ${!hasNextTask ? 'pick-next-btn--empty' : ''} ${nextIsIntervention ? 'pick-next-btn--intervention' : ''}`}
        onClick={handlePickNext}
        disabled={!hasNextTask || pickingNext}
      >
        {pickingNext ? (
          <span className='loading-spinner' />
        ) : (
          <ChevronRightIcon />
        )}
        <span>{pickNextLabel}</span>
      </button>
      {suggestedNextTask && (
        <div className='pick-next-hint'>
          <span className='pick-next-hint-label'>Next for you:</span>
          <RequesterBadge task={suggestedNextTask} />
          <span className='pick-next-hint-title'>
            {suggestedNextTask.title}
          </span>
          <TaskLanguage task={suggestedNextTask} />
        </div>
      )}
    </div>
  );

  // One accepted/in-progress task tile: deadline AND activity, so a task that
  // is still "due in 53 min" but untouched for an hour reads as such.
  const ActiveTaskRow = ({ task }: { task: IdleTrackedTask }) => {
    const deadlineInfo = formatDeadline(task.deadline);
    return (
      <div
        className={`task-row active-task ${requesterTileClass(task)} ${idleTileClass(task)}`}
        onClick={() => task.threadId && handleOpenThread(task.threadId)}
      >
        <div className='task-type-icon'>
          <TaskTypeIcon taskType={task.taskType} />
        </div>
        <div className='task-info'>
          <span className='task-title'>
            <RequesterBadge task={task} />
            <PriorityBadge priority={task.priority} />
            {task.title}
          </span>
          <span className='task-meta'>
            <TaskLanguage task={task} />
            {task.acceptedByUserName && (
              <span className='task-worker'>{task.acceptedByUserName}</span>
            )}
            {deadlineInfo ? (
              <span className={`task-deadline ${deadlineInfo.className}`}>
                {deadlineInfo.text}
              </span>
            ) : (
              <span className='task-status-badge'>{task.status}</span>
            )}
            <IdleActivityPill task={task} />
          </span>
        </div>
      </div>
    );
  };

  const ActionErrorBanner = () =>
    actionError ? (
      <div className='action-error' role='alert'>
        <span>{actionError}</span>
        <button
          type='button'
          className='action-error-dismiss'
          onClick={() => setActionError(null)}
        >
          Dismiss
        </button>
      </div>
    ) : null;

  const AttentionStrip = () => (
    <NeedsAttentionStrip
      tasks={activeTasks}
      currentUserId={currentUserId}
      isAdmin={isAdmin}
      releasingId={releasingTaskId}
      onOpen={(task) => task.threadId && handleOpenThread(task.threadId)}
      onRelease={handleReleaseTask}
    />
  );

  // Shared active tasks list
  const ActiveTasksList = () => (
    <div className='tasks-section active-section'>
      <h4 className='section-title'>My Active Tasks</h4>
      {activeTasks.length === 0 ? (
        <div className='empty-state'>No active tasks</div>
      ) : (
        <div className='tasks-list'>
          {activeTasks.map((task) => (
            <ActiveTaskRow key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );

  // Reported issues (help_request) — only rendered for issue-report handlers.
  // Pending ones are accepted on click (which opens the collaboration thread);
  // ones already picked up open their thread.
  const ReportedIssuesSection = () =>
    canSeeIssueReports ? (
      <div className='tasks-section issues-section'>
        <h4 className='section-title'>Reported Issues ({issueTasks.length})</h4>
        {issueTasks.length === 0 ? (
          <div className='empty-state'>No reported issues</div>
        ) : (
          <div className='tasks-list'>
            {issueTasks.map((task) => {
              const deadlineInfo = formatDeadline(task.deadline);
              return (
                <div
                  key={task.id}
                  className={`task-row issue-task status-${task.status}`}
                  onClick={() =>
                    task.threadId
                      ? handleOpenThread(task.threadId)
                      : handleAcceptTask(task.id)
                  }
                >
                  <div className='task-type-icon'>
                    <TaskTypeIcon taskType={task.taskType} />
                  </div>
                  <div className='task-info'>
                    <span className='task-title'>
                      <RequesterBadge task={task} />
                      <PriorityBadge priority={task.priority} />
                      {task.title}
                    </span>
                    <span className='task-meta'>
                      <span className='task-status-badge'>
                        {task.status === 'pending' ? 'New' : task.status}
                      </span>
                      {task.acceptedByUserName && (
                        <span className='task-worker'>
                          {task.acceptedByUserName}
                        </span>
                      )}
                      {deadlineInfo && (
                        <span
                          className={`task-deadline ${deadlineInfo.className}`}
                        >
                          {deadlineInfo.text}
                        </span>
                      )}
                    </span>
                  </div>
                  {task.status === 'pending' && (
                    <button
                      className='accept-btn'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptTask(task.id);
                      }}
                      disabled={acceptingTaskId === task.id}
                      title='Pick up issue'
                    >
                      {acceptingTaskId === task.id ? (
                        <span className='loading-spinner' />
                      ) : (
                        <ChevronRightIcon />
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    ) : null;

  // Shared completed tasks section
  const CompletedTasksSection = () =>
    showCompleted ? (
      <div className='tasks-section completed-section'>
        <h4 className='section-title'>
          Completed / Handed Off / Failed / Cancelled
        </h4>
        {completedLoading ? (
          <div className='empty-state'>Loading...</div>
        ) : completedTasks.length === 0 ? (
          <div className='empty-state'>No completed tasks</div>
        ) : (
          <div className='tasks-list'>
            {completedTasks.map((task) => (
              <div
                key={task.id}
                className={`task-row completed-task status-${task.status}`}
                onClick={() =>
                  task.threadId && handleOpenThread(task.threadId, true)
                }
              >
                <div className='task-type-icon'>
                  <TaskTypeIcon taskType={task.taskType} />
                </div>
                <div className='task-info'>
                  <span className='task-title'>{task.title}</span>
                  <span className='task-meta'>
                    <TaskLanguage task={task} />
                    {task.acceptedByUserName && (
                      <span className='task-worker'>
                        {task.acceptedByUserName}
                      </span>
                    )}
                    <span className={`task-status-badge status-${task.status}`}>
                      {task.status}
                    </span>
                  </span>
                </div>
                <span className='task-date'>
                  {new Date(
                    task.updatedAt || task.createdAt,
                  ).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

  // Row actions shared by the Available / In Progress / mine sections.
  const renderInterventionActions = (intervention: Intervention) => {
    const href = interventionOperatorHref(intervention);
    return (
      <>
        {intervention.bitwardenCredentialId && (
          <button
            className='connect-btn'
            onClick={() => handleRevealCredentials(intervention.commandId)}
            disabled={credLoading === intervention.commandId}
          >
            {credLoading === intervention.commandId
              ? 'Loading...'
              : revealedCreds[intervention.commandId]
                ? 'Refresh'
                : 'Credentials'}
          </button>
        )}
        {href && (
          <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='connect-btn'
            onClick={() => handleConnectIntervention(intervention.commandId)}
          >
            {intervention.status === 'active' &&
            intervention.claimedBy === currentUserId
              ? 'Reopen'
              : 'Connect'}
          </a>
        )}
        {!usesOperatorPage(intervention) && (
          <button
            className='resolve-btn'
            onClick={() => handleResolveIntervention(intervention.commandId)}
            disabled={resolvingId === intervention.commandId}
          >
            {resolvingId === intervention.commandId
              ? 'Resolving...'
              : 'Resolve'}
          </button>
        )}
      </>
    );
  };

  const renderInterventionCredentials = (intervention: Intervention) => (
    <>
      {credError[intervention.commandId] && (
        <div className='credential-error'>
          {credError[intervention.commandId]}
        </div>
      )}
      {revealedCreds[intervention.commandId] &&
        revealedCreds[intervention.commandId].length > 0 && (
          <div className='credential-reveal-section'>
            {revealedCreds[intervention.commandId].map((cred) => (
              <div key={cred.credentialId} className='credential-card'>
                <div className='credential-meta'>
                  <span className='credential-label'>{cred.label}</span>
                  {cred.site && (
                    <span className='credential-site'>{cred.site}</span>
                  )}
                </div>
                {cred.error && (
                  <div className='credential-error'>{cred.error}</div>
                )}
                {cred.fields && (
                  <div className='credential-fields'>
                    {Object.entries(cred.fields).map(([key, raw]) => {
                      const value = credentialFieldText(raw);
                      const fieldKey = `${cred.credentialId}-${key}`;
                      const isVisible = visibleFields.has(fieldKey);
                      return (
                        <div key={key} className='credential-field-row'>
                          <span className='credential-field-label'>{key}</span>
                          <span className='credential-field-value'>
                            {isVisible ? value : maskValue(value)}
                          </span>
                          <button
                            className='credential-toggle-btn'
                            onClick={() => toggleFieldVisibility(fieldKey)}
                          >
                            {isVisible ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </>
  );

  // ─── Ops Worker View: simple pick-next + active tasks ───────────
  if (!isAdmin) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='page-header-area'>
            <div className='title-and-text'>
              <h3>Tasks</h3>
            </div>
            <div className='header-actions'>
              <OperatorLanguages onSaved={() => loadTasks()} />
              <button
                className={`completed-toggle-btn ${showCompleted ? 'active' : ''}`}
                onClick={() => setShowCompleted((prev) => !prev)}
              >
                {showCompleted ? 'Hide Completed' : 'Show Completed'}
              </button>
            </div>
          </div>

          <div className='worker-view'>
            <ActionErrorBanner />
            <AttentionStrip />
            <PickNextButton />
            <ReportedIssuesSection />
            <BrowserInterventionsSection
              title='My Browser Interventions'
              variant='active'
              interventions={myActiveInterventions}
              currentUserId={currentUserId}
              renderActions={renderInterventionActions}
              renderDetails={renderInterventionCredentials}
            />
            <ActiveTasksList />
            <CompletedTasksSection />
          </div>
        </div>
      </div>
    );
  }

  // ─── Ops Admin View: pick-next + full visibility ────────────────
  return (
    <div className='page-wrapper'>
      <div className='page-inner-wrapper human-tasks-page'>
        <div className='page-header-area'>
          <div className='title-and-text'>
            <h3>Human Tasks</h3>
          </div>
          <div className='header-actions'>
            <OperatorLanguages onSaved={() => loadTasks()} />
            <button
              className={`completed-toggle-btn ${showCompleted ? 'active' : ''}`}
              onClick={() => setShowCompleted((prev) => !prev)}
            >
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </button>
          </div>
        </div>

        <ActionErrorBanner />
        <AttentionStrip />
        <PickNextButton />

        <div className='tasks-container'>
          <ReportedIssuesSection />

          {/* Available Tasks Section */}
          <div className='tasks-section available-section'>
            <h4 className='section-title'>Available</h4>
            {availableTasks.length === 0 ? (
              <div className='empty-state'>No tasks available</div>
            ) : (
              <div className='tasks-list'>
                {availableTasks.map((task) => {
                  const deadlineInfo = formatDeadline(task.deadline);
                  const isSuggested = task.id === suggestedNextTask?.id;
                  return (
                    <div
                      key={task.id}
                      className={`task-row available-task ${isSuggested ? 'available-task--suggested' : ''} ${requesterTileClass(task)}`}
                    >
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>
                          <RequesterBadge task={task} />
                          <PriorityBadge priority={task.priority} />
                          <RetryBadge task={task} />
                          {task.title}
                          {isSuggested && (
                            <span
                              className='task-next-badge'
                              title='What Pick Next Task would give you'
                            >
                              Next for you
                            </span>
                          )}
                        </span>
                        <span className='task-meta'>
                          <TaskLanguage task={task} />
                          {deadlineInfo ? (
                            <span
                              className={`task-deadline ${deadlineInfo.className}`}
                            >
                              {deadlineInfo.text}
                            </span>
                          ) : (
                            <span className='task-date'>
                              {new Date(task.createdAt).toLocaleDateString()}
                            </span>
                          )}
                        </span>
                      </div>
                      <button
                        className='accept-btn'
                        onClick={() => handleAcceptTask(task.id)}
                        disabled={acceptingTaskId === task.id}
                        title='Accept Task'
                      >
                        {acceptingTaskId === task.id ? (
                          <span className='loading-spinner' />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Browser Interventions: unpicked first, then everything in progress */}
          <BrowserInterventionsSection
            title='Browser Interventions — Available'
            variant='available'
            interventions={pendingInterventions}
            currentUserId={currentUserId}
            renderActions={renderInterventionActions}
            renderDetails={renderInterventionCredentials}
          />
          <BrowserInterventionsSection
            title='Browser Interventions — In Progress'
            variant='active'
            interventions={inProgressInterventions}
            currentUserId={currentUserId}
            renderActions={renderInterventionActions}
            renderDetails={renderInterventionCredentials}
          />

          {/* Active Tasks Section */}
          <div className='tasks-section active-section'>
            <h4 className='section-title'>Active</h4>
            {activeTasks.length === 0 ? (
              <div className='empty-state'>No active tasks</div>
            ) : (
              <div className='tasks-list'>
                {activeTasks.map((task) => (
                  <ActiveTaskRow key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>

          {/* Completed/Failed/Cancelled Tasks Section */}
          <CompletedTasksSection />
        </div>
      </div>
    </div>
  );
};

export default HumanTasks;
